import React, { useEffect, useState } from 'react';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import {
    selectHasCapability,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import { getExtensionKeyFromSession } from 'flow/selectors/selectors';

import { TypedMap } from 'flow/types/TypedMap';
import { ChatSession } from 'flow/types/Chats';
import useAppContext from 'flow/hooks/useAppContext';
import { usePrevious } from 'flow/hooks';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { fetchCapabilitiesForExtension } from 'flow/actions/app-actions';
import { Capabilities } from 'flow/utils/capability-utils';

interface Props {
    session: TypedMap<ChatSession>;
}

const NoChatProfileBanner = ({ session }: Props) => {
    const { i18next } = useAppContext();

    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const dispatch = useAppDispatch();
    const extensionKey = getExtensionKeyFromSession(session, ownExtensionKey);

    const prevExtension = usePrevious(extensionKey);
    const roomType = session.get('roomType');

    useEffect(() => {
        if (prevExtension !== extensionKey && roomType === 'session') {
            dispatch(fetchCapabilitiesForExtension(extensionKey));
        }
    }, [extensionKey, prevExtension]);

    const hasCapabilityChat = useAppSelector((state) =>
        selectHasCapability(state, extensionKey, Capabilities.CHAT),
    );
    const [showBanner, setShowBanner] = useState(false);

    useEffect(
        () => setShowBanner(!hasCapabilityChat),
        [hasCapabilityChat, session],
    );

    if (hasCapabilityChat || !showBanner) {
        return null;
    }

    return (
        <div className="flex justify-center">
            <div className="flex items-center gap-x-2 rounded-sm bg-gray-800 px-2 py-3 text-white">
                <p className="p-1 text-sm/4 font-bold">
                    {i18next.t('chat.no-capability.banner')}
                </p>

                <IconButton
                    aria-label={i18next.t('modal.close')}
                    className="ms-4"
                    onClick={() => setShowBanner(false)}
                >
                    <Icon iconKey="close" className="text-gray-400" />
                </IconButton>
            </div>
        </div>
    );
};

export default NoChatProfileBanner;
